import { Button } from '@mui/material';
import { forwardRef, Ref, useImperativeHandle, useMemo, useRef } from 'react';

import {
  EditCalendarReminderForm,
  EditCalendarReminderFormProps,
  EditCalendarReminderFormRef,
  EditCalendarReminderFormValues,
} from './EditCalendarReminderForm';

import { Dialog, DialogRef } from '@/components/common/Dialog';
import {
  CalendarEntryType,
  CalendarReminder,
  CreateCalendarReminder,
  GenericCalendarEntry,
  UpdateCalendarReminder,
} from '@/models/CalendarEntry';
import { useTranslationPrefix } from '@/translations/i18n';

export type EditCalendarReminderDialogRef = DialogRef;

export interface EditCalendarReminderDialogProps<T extends EditCalendarReminderFormValues> {
  /**
   * The selected calendar entry. Can be a `CalendarReminder` or not. Should not be provided when creating a new entry.
   */
  selectedCalendarEntry: T extends CreateCalendarReminder
    ? undefined
    : T extends UpdateCalendarReminder
    ? GenericCalendarEntry | null
    : never;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: T extends CreateCalendarReminder
    ? (values: CreateCalendarReminder) => void
    : T extends UpdateCalendarReminder
    ? (id: string, values: UpdateCalendarReminder) => void
    : never;
  /**
   * Whether the dialog is loading or not. If true, the dialog will be disabled and the submit button will be loading.
   */
  isLoading?: boolean;
}

const EditCalendarReminderDialogComponent = <T extends EditCalendarReminderFormValues>(
  { selectedCalendarEntry, onSubmit, isLoading }: EditCalendarReminderDialogProps<T>,
  ref: Ref<EditCalendarReminderDialogRef>,
) => {
  const dialogRef = useRef<DialogRef>(null);
  const formRef = useRef<EditCalendarReminderFormRef<T>>(null);
  const selectedCalendarReminder = useMemo(
    () =>
      (selectedCalendarEntry
        ? selectedCalendarEntry.type === CalendarEntryType.Reminder
          ? (selectedCalendarEntry as CalendarReminder)
          : null
        : undefined) as EditCalendarReminderFormProps<T>['reminder'],
    [selectedCalendarEntry],
  );
  const isNewReminder = selectedCalendarReminder === undefined;
  const t = useTranslationPrefix(
    `home.calendar.reminders.${isNewReminder ? 'create_dialog' : 'edit_dialog'}`,
  );

  const onFormSubmit = (values: T) => {
    if (isNewReminder) {
      // Create
      (onSubmit as EditCalendarReminderDialogProps<CreateCalendarReminder>['onSubmit'])(
        values as CreateCalendarReminder,
      );
    } else {
      // Update
      selectedCalendarReminder &&
        (onSubmit as EditCalendarReminderDialogProps<UpdateCalendarReminder>['onSubmit'])(
          selectedCalendarReminder.id,
          values as UpdateCalendarReminder,
        );
    }
  };

  const resetForm = () => {
    formRef.current?.resetForm();
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => dialogRef.current!);

  return (
    <Dialog
      ref={dialogRef}
      title={t('title')}
      actions={
        <>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            disabled={isLoading}
            onClick={() => dialogRef.current?.close()}>
            {t('cancel')}
          </Button>
          <Button
            size="small"
            fullWidth
            loading={isLoading}
            onClick={() => formRef.current?.submitForm()}>
            {t('submit')}
          </Button>
        </>
      }
      onClose={resetForm}
      disableClose={isLoading}>
      <EditCalendarReminderForm
        ref={formRef}
        reminder={selectedCalendarReminder}
        onSubmit={onFormSubmit}
      />
    </Dialog>
  );
};

export const EditCalendarReminderDialog = forwardRef(EditCalendarReminderDialogComponent) as <
  T extends EditCalendarReminderFormValues,
>(
  props: EditCalendarReminderDialogProps<T> & { ref?: Ref<EditCalendarReminderDialogRef> },
) => ReturnType<typeof EditCalendarReminderDialogComponent<T>>;
